import { useState, useEffect, useCallback } from "react"
import { constants } from "../../../constants/common"

// Type definitions
interface Constraints {
  dislikes: string[]
  likes: string[]
  avoid_categories: string[]
  notes: string[]
}

interface MedicalInfo {
  concerning_parameters: string[]
  dietary_restrictions: string[]
  medical_considerations: string[]
}

interface Step2Data {
  diet_type: "vegetarian" | "non_vegetarian" | "vegan" | "eggetarian"
  cuisine_pref: string[]
  constraints: Constraints
  medical_info: MedicalInfo
}

interface UsePreferencesStepProps {
  initialData: Partial<Step2Data>
  apiData?: any
  selectedDropdownOption?: string
  onDataUpdate?: (data: Partial<Step2Data>) => void
}

export const usePreferencesStep = ({
  initialData,
  apiData,
  selectedDropdownOption,
  onDataUpdate,
}: UsePreferencesStepProps) => {
  const [formData, setFormData] = useState<Partial<Step2Data>>({
    diet_type: "vegetarian",
    cuisine_pref: [],
    constraints: {
      dislikes: [],
      likes: [],
      avoid_categories: [],
      notes: [],
      ...initialData.constraints,
    },
    medical_info: {
      concerning_parameters: [],
      dietary_restrictions: [],
      medical_considerations: [],
      ...initialData.medical_info,
    },
    ...initialData,
  })

  // Add a flag to track if user has made changes
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false)

  // Input states for adding new items
  const [newDislike, setNewDislike] = useState("")
  const [newLike, setNewLike] = useState("")
  const [newAvoidCategory, setNewAvoidCategory] = useState("")
  const [newNote, setNewNote] = useState("")
  const [newConcerningParam, setNewConcerningParam] = useState("")
  const [newMedicalConsideration, setNewMedicalConsideration] = useState("")

  // Function to normalize dietary restrictions for consistent comparison
  const normalizeDietaryRestrictions = (restrictions: string[]) => {
    return restrictions.map(restriction => restriction.toLowerCase().trim())
  }

  // Map API data to component data structure
  const mapApiDataToFormData = useCallback((apiData: any) => {
    // Check for different possible data structures
    let profile = null
    
    if (selectedDropdownOption && selectedDropdownOption !== constants.DEFAULT) {
      // Use the selected diet plan's profile
      const selectedPlan = apiData?.top_5_regenerated_diet_plans?.find(
        (plan: any) => plan.generation_id === selectedDropdownOption
      )
      if (selectedPlan?.patient_profile_v0 && selectedPlan.status === constants.SUCCESS) {
        profile = selectedPlan.patient_profile_v0
      }
    } else {
      // Use default profile (first available)
      if (apiData?.diet_plan?.patient_profile_v0) {
        profile = apiData.diet_plan.patient_profile_v0
      } else if (apiData?.top_5_regenerated_diet_plans?.[0]?.patient_profile_v0) {
        profile = apiData.top_5_regenerated_diet_plans[0].patient_profile_v0
      } else if (apiData?.patient_profile_v0) {
        profile = apiData.patient_profile_v0
      } else {
        return
      }
    }

    // Map diet type
    const diet_type = profile.diet_type || "vegetarian"

    // Map cuisine preferences
    const cuisine_pref = profile.cuisine_pref || []

    // Map constraints
    const constraints = {
      dislikes: profile.constraints?.dislikes || [],
      likes: profile.constraints?.likes || [],
      avoid_categories: profile.constraints?.avoid_categories || [],
      notes: profile.constraints?.notes || [],
    }

    // Map medical information from patient_medical_test_information
    const medical_info = {
      concerning_parameters: profile.patient_medical_test_information?.concerning_parameters || [],
      dietary_restrictions: profile.patient_medical_test_information?.dietary_restrictions || [],
      medical_considerations: profile.patient_medical_test_information?.medical_considerations || [],
    }

    return {
      diet_type,
      cuisine_pref,
      constraints,
      medical_info,
    }
  }, [selectedDropdownOption])

  useEffect(() => {
    // Only initialize form data if user hasn't made changes yet
    if (hasUserMadeChanges) {
      return
    }

    // First, try to use API data if available
    if (apiData) {
      const mappedData = mapApiDataToFormData(apiData)
      if (mappedData) {
        setFormData(mappedData)
        return
      }
    }

    // Fallback to initialData if no API data
    if (initialData && Object.keys(initialData).length > 0) {
      const newFormData = {
        diet_type: initialData.diet_type || "vegetarian" as const,
        cuisine_pref: initialData.cuisine_pref || [],
        constraints: {
          dislikes: initialData.constraints?.dislikes || [],
          likes: initialData.constraints?.likes || [],
          avoid_categories: initialData.constraints?.avoid_categories || [],
          notes: initialData.constraints?.notes || [],
        },
        medical_info: {
          concerning_parameters: initialData.medical_info?.concerning_parameters || [],
          dietary_restrictions: initialData.medical_info?.dietary_restrictions || [],
          medical_considerations: initialData.medical_info?.medical_considerations || [],
        },
      }
      setFormData(newFormData)
    }
  }, [initialData, apiData, hasUserMadeChanges, mapApiDataToFormData])

  // Notify parent component when formData changes
  useEffect(() => {
    if (onDataUpdate && Object.keys(formData).length > 0 && hasUserMadeChanges) {
      onDataUpdate(formData)
    }
  }, [formData, onDataUpdate, hasUserMadeChanges])

  const addToArray = (
    category: "dislikes" | "likes" | "avoid_categories" | "notes" | "concerning_parameters" | "medical_considerations",
    value: string,
    setter: (value: string) => void,
  ) => {
    if (value.trim()) {
      setHasUserMadeChanges(true) // Mark that user has made changes
      
      // Check if category belongs to constraints
      const isConstraintCategory = ["dislikes", "likes", "avoid_categories", "notes"].includes(category)
      
      if (isConstraintCategory) {
        setFormData((prev: any) => {
          const newData = {
            ...prev,
            constraints: {
              ...prev.constraints!,
              [category]: [...(prev.constraints![category as keyof typeof prev.constraints] || []), value.trim()],
            },
          }
          return newData
        })
      } else {
        setFormData((prev: any) => {
          const newData = {
            ...prev,
            medical_info: {
              ...prev.medical_info!,
              [category]: [...(prev.medical_info![category as keyof typeof prev.medical_info] || []), value.trim()],
            },
          }
          return newData
        })
      }
      setter("")
    }
  }

  const removeFromArray = (
    category: "dislikes" | "likes" | "avoid_categories" | "notes" | "concerning_parameters" | "medical_considerations",
    index: number,
  ) => {
    setHasUserMadeChanges(true) // Mark that user has made changes
    
    if (category in (formData.constraints || {})) {
      setFormData((prev: any) => ({
        ...prev,
        constraints: {
          ...prev.constraints!,
          [category]: prev.constraints![category as keyof typeof prev.constraints]?.filter((_: any, i: number) => i !== index) || [],
        },
      }))
    } else {
      setFormData((prev: any) => ({
        ...prev,
        medical_info: {
          ...prev.medical_info!,
          [category]:
            prev.medical_info![category as keyof typeof prev.medical_info]?.filter((_: any, i: number) => i !== index) || [],
        },
      }))
    }
  }

  const toggleCuisine = (cuisine: string) => {
    setHasUserMadeChanges(true) // Mark that user has made changes
    setFormData((prev: any) => {
      const newData = {
        ...prev,
        cuisine_pref: prev.cuisine_pref?.includes(cuisine)
          ? prev.cuisine_pref.filter((c: string) => c !== cuisine)
          : [...(prev.cuisine_pref || []), cuisine],
      }
      return newData
    })
  }

  const toggleDietaryRestriction = (restriction: string) => {
    setHasUserMadeChanges(true) // Mark that user has made changes
    setFormData((prev: any) => {
      const currentRestrictions = prev.medical_info?.dietary_restrictions || []
      const normalizedCurrent = normalizeDietaryRestrictions(currentRestrictions)
      const isCurrentlySelected = normalizedCurrent.includes(restriction.toLowerCase())
      
      const newRestrictions = isCurrentlySelected
        ? currentRestrictions.filter((r: string) => r.toLowerCase() !== restriction.toLowerCase())
        : [...currentRestrictions, restriction]
      
      return {
        ...prev,
        medical_info: {
          ...prev.medical_info!,
          dietary_restrictions: newRestrictions,
        },
      }
    })
  }

  const isFormValid = () => {
    return formData.diet_type && formData.cuisine_pref && formData.cuisine_pref.length > 0
  }

  // Function to reset form to API data
  const resetToApiData = () => {
    setHasUserMadeChanges(false)
    if (apiData) {
      const mappedData = mapApiDataToFormData(apiData)
      if (mappedData) {
        setFormData(mappedData)
      }
    }
  }

  const handleSubmit = (onComplete: (data: Step2Data) => void) => {
    // Ensure all required fields are present
    const completeFormData: Step2Data = {
      diet_type: formData.diet_type || "vegetarian",
      cuisine_pref: formData.cuisine_pref || [],
      constraints: {
        dislikes: formData.constraints?.dislikes || [],
        likes: formData.constraints?.likes || [],
        avoid_categories: formData.constraints?.avoid_categories || [],
        notes: formData.constraints?.notes || [],
      },
      medical_info: {
        concerning_parameters: formData.medical_info?.concerning_parameters || [],
        dietary_restrictions: formData.medical_info?.dietary_restrictions || [],
        medical_considerations: formData.medical_info?.medical_considerations || [],
      },
    }
    
    if (isFormValid()) {
      onComplete(completeFormData)
    }
  }

  const updateDietType = (value: "vegetarian" | "non_vegetarian" | "vegan" | "eggetarian") => {
    setHasUserMadeChanges(true) // Mark that user has made changes
    setFormData((prev: any) => {
      const newData = { ...prev, diet_type: value }
      return newData
    })
  }

  return {
    formData,
    hasUserMadeChanges,
    newDislike,
    setNewDislike,
    newLike,
    setNewLike,
    newAvoidCategory,
    setNewAvoidCategory,
    newNote,
    setNewNote,
    newConcerningParam,
    setNewConcerningParam,
    newMedicalConsideration,
    setNewMedicalConsideration,
    normalizeDietaryRestrictions,
    addToArray,
    removeFromArray,
    toggleCuisine,
    toggleDietaryRestriction,
    isFormValid,
    resetToApiData,
    handleSubmit,
    updateDietType,
  }
}
